import React, { useEffect, useState, useRef, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '../../../images/icon-search.svg';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { getvisitorList } from '../../../redux/visitor-list/actions/visitor-list.action';
import { AvailabilityRedirectionContext } from '../../context/availabilityRedirection';
import { ISelectUser, visitorObj } from '../interface/Visitor.interface';
// import { visitorObj } from '../VisitorAppointment/visitorAppointment';
import styles from './SelectUser.module.css';
import TinyLoader from '../../common/tiny-loader/TinyLoader';
import { useDebounce } from '../../../utils/useDebounce';

export const SelectUser = ({
    setShowSearchResult,
    setSelectedUser,
    setUserId,
    setUserData,
    showSearchResult,
    searchText,
    setSearchText,
}: ISelectUser) => {
    const dispatch = useDispatch<AppDispatch>();
    const availabilityRedirectionContext = useContext(AvailabilityRedirectionContext);
    const userRef = useRef<HTMLInputElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchResult, setSearchResult] = useState<visitorObj[] | null>(null);
    const [inputValue, setInputValue] = useState(searchText || '');
    const searchWithDebounce = useDebounce(inputValue, 1000);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const { data: visitorsList, loading: visitorsListLoading } = useSelector((state: IRootState) => state.VisitorListReducer);

    useEffect(() => {
        document.addEventListener('mousedown', (event: any) => {
            if (userRef?.current && !userRef?.current.contains(event.target)) {
                setShowSearchResult(false);
            }
        });
    }, []);

    // Use debounced searchText for API calls only
    useEffect(() => {
        if (searchWithDebounce && searchWithDebounce.trim().length >= 3) {
            dispatch(
                getvisitorList({
                    page: 1,
                    searchText: searchWithDebounce,
                }),
            );
        } else if (searchWithDebounce === '' || (searchWithDebounce && searchWithDebounce.trim().length < 3)) {
            // Clear results when search is too short or empty
            setSearchResult(null);
        }
    }, [searchWithDebounce]);

    useEffect(() => {
        // Only update searchResult after API response
        if (visitorsList?.data !== undefined) {
            setSearchResult(visitorsList.data || []);
        }
    }, [visitorsList]);

    // Clear results when debounced searchText is cleared
    useEffect(() => {
        if (searchText === '') {
            setSearchResult(null);
            // Only clear input if it's actually empty (don't interfere with user typing)
            if (inputValue === '') {
                setInputValue('');
            }
        }
    }, [searchText]);

    const normalizeSearchValue = (value: string) => {
        return value
            .replace(/^\s+/, '') 
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ' ' && inputValue && inputValue.length === 0) {
            event.preventDefault();
        }
    };

    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        const rawValue = (e.target as HTMLInputElement).value;
        const value = normalizeSearchValue(rawValue);

        // Update local input immediately
        setInputValue(value);

        // Update parent (debounced)
        setSearchText(value);

        // Show dropdown only if valid length
        value.length >= 3 ? setShowSearchResult(true) : setShowSearchResult(false);
    };


    const handleSearchItem = (item: visitorObj) => {
        availabilityRedirectionContext?.setSelectedUserTableData({
            name: `${item.firstName}  ${item.lastName}`,
            bedrooms: item.desiredNumBedrooms,
            contact: item.phoneNumber,
            date: item.expectedMoveInDate,
            email: item.email,
            countryCode: item.countryCode,
            locationName: item.locationName,
            companyName: item.companyName,
        });
        availabilityRedirectionContext?.setUserId(item.id);
        availabilityRedirectionContext?.setAvailabilityData({
            locationId: '',
            locationName: '',
            selectedDate: '',
            slotRange: '',
            unitId: '',
            unitName: '',
            lockerNumber: 0,
        });
        availabilityRedirectionContext?.setStep(1);
        setUserId(item.id);
        setShowSearchResult(false);
        setSelectedUser(`${item.firstName}  ${item.lastName}`);
        setUserData({
            name: `${item.firstName}  ${item.lastName}`,
            bedrooms: item.desiredNumBedrooms,
            contact: item.phoneNumber,
            date: item.expectedMoveInDate,
            email: item.email,
            countryCode: item.countryCode,
            locationName: item.locationName,
            companyName: item.companyName,
        });
        const selectedName = `${item.firstName}  ${item.lastName}`;
        // setInputValue(selectedName);
        setInputValue('');
        setSearchText(selectedName);
    };

    return (
        <div className={`${styles.searchContainer} user-select-container`} ref={userRef}>
            <div className={styles.searchBox}>
                <input
                    ref={inputRef}
                    onKeyPress={handleKeyPress}
                    value={inputValue}
                    onChange={(e) => handleChange(e)}
                    onPaste={(e: React.ClipboardEvent) => {
                        e.preventDefault();
                        const pastedValue = e?.clipboardData?.getData('text/plain')?.trimEnd() || '';
                        setInputValue(pastedValue);
                        setSearchText(pastedValue);
                        if (pastedValue.trim().length >= 3) {
                            setShowSearchResult(true);
                        }
                    }}
                    type="text"
                    placeholder="Type at least 3 characters to search..."
                    maxLength={50}
                />
                <img className={styles.searchIconImage} src={SearchIcon} alt="search-icon" />
            </div>
            {!visitorsListLoading && showSearchResult && searchText && searchText.trim().length >= 3 && (
                <>
                    {searchResult && searchResult.length > 0 ? (
                        <div className={styles.searchResultContainer}>
                            {searchResult.map((item: visitorObj) => {
                                return (
                                    <div className={`${styles.searchItem} ${item?.deActivateDate !== null ? styles.deactivate : ''}`} key={item.id} onClick={() => { item.deActivateDate === null && handleSearchItem(item) }}>
                                        <div className={styles.searchItemContent}>
                                            <span>{`${item.firstName}  ${item.lastName}`}</span>
                                            <span>{`(${item.email})`}</span>
                                        </div>
                                        {item?.deActivateDate && <span className={styles.deactiveText}>Deactivated</span>}
                                    </div>
                                );
                            })}
                        </div>
                    ) : searchResult !== null && searchResult !== undefined && searchResult.length === 0 ? (
                        <div className={styles.searchResultContainer}>
                            <p className={styles.noSearchResultText}>{`No search result found with "${searchText}"`}</p>
                        </div>
                    ) : null}
                </>
            )}

            {visitorsListLoading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingContent}>
                        <TinyLoader />
                        <p className={styles.loadingText}>Searching visitors...</p>
                    </div>
                </div>
            )}
        </div>
    );
};
